import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {
    IBusinessPartner,
    IBusinessPartnerAddress,
    IBusinessPartnerSalesArea, IPaymentTerms
} from "niteroi/packages/sales-distribution/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import Event from "sap/ui/base/Event";
import Table, {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import GenericTile from "sap/m/GenericTile";
import CustomData from "sap/ui/core/CustomData";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Sorter from "sap/ui/model/Sorter";
import {ListBase$DeleteEvent} from "sap/m/ListBase";
import Select, {Select$ChangeEvent} from "sap/m/Select";
import TableSelectDialog, {TableSelectDialog$ConfirmEvent} from "sap/m/TableSelectDialog";
import Input, {Input$ValueHelpRequestEvent} from "sap/m/Input";
import {InputBase$ChangeEvent} from "sap/m/InputBase";
import {ILoadDataResponse, IResponseTextError, IVIESResponse} from "niteroi/packages/system/models/interfaces";

export default class BusinessPartners extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);
    private readonly updateAnalyticsHandler = this.updateAnalytics.bind(this);
    private _valueHelpRequestPath: string;

    public onInit(): void {
        super.onInit();

        const partnersModel = this.getModel("salesDistributionBusinessPartnerCollection");
        partnersModel?.detachRequestCompleted(this.updateAnalyticsHandler, this);
        partnersModel?.attachRequestCompleted(this.updateAnalyticsHandler, this);

        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            this.getView().setBusy(true);
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
        this.initializeAnalyticsModel();
    }

    public onExit(): void {
        this.getModel("salesDistributionBusinessPartnerCollection")?.detachRequestCompleted(this.updateAnalyticsHandler, this);
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");
        if (routeName === "sales-distribution/business-partners/add") {
            this.getModel("salesDistributionBusinessPartnerDetail").setData(this.getBlankDocumentModel());
            this.updateAnalytics();
            this.getView().setBusy(false);
            return;
        } else {
            const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
            this.getData(id);
        }
    }

    protected getData(objectId: string = ""): void {
        const componentRoute = "/api/sales-distribution/business-partners";
        const url = objectId ? `${componentRoute}/${objectId}` : componentRoute;
        const headers = this.getRequestHeaders();
        const requestModel = this.createRequestModel();
        requestModel.loadData(url, "", true, "GET", false, true, headers).then(() => {
            const modelData = requestModel.getData();
            const modelName = objectId ? "salesDistributionBusinessPartnerDetail" : "salesDistributionBusinessPartnerCollection";
            const model = this.getModel(modelName);
            model.setData(modelData);
            model.refresh(true);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.updateAnalytics();
            this.getView().setBusy(false);
        });
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idBusinessPartnersTable", ["customerNumber", "legalName1", "legalName2", "vatId", "taxNumber", "partnerRole"]);
    }

    /* ---------------------------------------------------------------------- *
     *  Navigation & lifecycle
     * ---------------------------------------------------------------------- */

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/business-partners", {}, false);
    }

    public onCreateBusinessPartnerPress(): void {
        this.navTo("sales-distribution/business-partners/add", {}, true);
    }

    public onRefreshBusinessPartnersPress(): void {
        this.getModel("salesDistributionBusinessPartnerCollection")?.refresh(true);
        this.updateAnalytics();
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selected = rowContext?.getObject() as IBusinessPartner | undefined;
        if (!selected?.businessPartnerId) {
            return;
        }
        this.navTo("sales-distribution/business-partners/edit", {id: selected.businessPartnerId}, false);
    }

    private initializeAnalyticsModel(): void {
        if (!this.getModel("salesDistributionBusinessPartnersAnalytics")) {
            this.setModel(new JSONModel(this.getDefaultAnalytics()), "salesDistributionBusinessPartnersAnalytics");
        }

        this.updateAnalytics();
    }

    /* ---------------------------------------------------------------------- *
     *  Persistence
     * ---------------------------------------------------------------------- */

    public onPressSavePartner(): void {
        this.getView().setBusy(true);
        const businessPartnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
        const partner = businessPartnerModel.getData() as IBusinessPartner;

        const inputVatId = (this.byId("salesDistributionBusinessPartnerInputVatId") as Input);
        if (!partner.legalName1 || !partner.vatId || inputVatId.getValueState() === "Error") {
            this.getView().setBusy(false);
            return;
        }

        const componentRoute = "/api/sales-distribution/business-partners";
        const url = partner.businessPartnerId ? `${componentRoute}/${partner.businessPartnerId}` : componentRoute;
        const method = partner.businessPartnerId ? "PUT" : "POST";
        businessPartnerModel.loadData(url, JSON.stringify(partner), true, method, false, true, this.getRequestHeaders()).then(() => {
            if (method === "POST") {
                this.navTo("sales-distribution/business-partners/edit", { id: businessPartnerModel.getProperty("/businessPartnerId") }, false);
            }
            (this.byId("salesDistributionBusinessPartnerInputVatId") as Input).setValueState("None");
            (this.byId("salesDistributionBusinessPartnerRole") as Select).setValueState("None");
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText) as IResponseTextError;
            this.showToast(this.getText("msgSalesDistributionBusinessPartnerSaveError", [responseText.code, responseText.error]));
            if (responseText.code === 409) {
                inputVatId.setValueState("Warning");
                (this.byId("salesDistributionBusinessPartnerRole") as Select).setValueState("Warning");
            }
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onPressToggleBlock(): void {
        const model = this.getModel("salesDistributionBusinessPartnerDetail");
        const active = model.getProperty("/isActive") as boolean;

        model.setProperty("/isActive", !active);
        this.onPressSavePartner();
    }

    public onChangeValueResetState(event: Select$ChangeEvent): void {
        const source = event.getSource();
        source.setValueState("None");
    }

    public onChangeInputVAT(event: InputBase$ChangeEvent): void {
        const source = event.getSource();
        (this.byId("salesDistributionBusinessPartnerRole") as Select).setValueState("None");
        source.setValueState("None").setValueStateText("").setShowValueStateMessage(false);
        const value = source.getValue();
        source.setBusyIndicatorDelay(0).setBusy(true);
        const verification = this.verifyVatWithVies(value);
        if (verification.valid === false) {
            source.setValueState("Error")
                .setShowValueStateMessage(true)
                .setValueStateText(verification.userError);
            source.setBusy(false).setBusyIndicatorDelay(1000);
            return;
        }

        const viesModel = new JSONModel({});
        const url = "/api/sales-distribution/business-partners/check-vat-number";
        const body = {country: verification.countryCode, number: verification.vatNumber};
        viesModel.loadData(url, JSON.stringify(body), true, "POST", true, undefined, this.getRequestHeaders()).then(() => {
            const response = viesModel.getData() as IVIESResponse;
            if (response.valid === false) {
                source.setValueState("Error").setValueStateText(response.userError).setShowValueStateMessage(true);
                return;
            }

            if (response.name && response.name !== "---") {
                (this.byId("salesDistributionBusinessPartnerInputLegalName1") as Input).setValue(response.name);
            }

            const partnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
            const addresses = partnerModel.getProperty("/addresses") as Array<IBusinessPartnerAddress>;
            if (response.address && response.address !== "---" && response.address !== "N/A") {
                // Portuguese addresses have the following structure: [STREET NUMBER]\n[COUNTY]\n[ZIP CITY]
                // Austrian addresses have the following structure: [STREET NUMBER]\n[COUNTRY-ZIP CITY]
                // "RUA VALE TRIGO +++ VILA NOVA DE FOZ CÔA +++ 5155-420 MÓS VLF"
                // "Schottenfeldgasse 23/3.OG  +++ AT-1070 Wien"
                // "EDF MARINA CLUB AVENIDA ARRIAGA N 73 1 - 105 +++ FUNCHAL +++ 9004-533 FUNCHAL"
                // @TODO - Refactor into a formatter
                const address = response.address.includes("\n") ? response.address.split("\n") : response.address;
                const newAddress = this.getBlankAddress();
                newAddress.countryCode = response.countryCode;
                newAddress.street = address[0];
                if (address instanceof Array && address.length > 2) {
                    address.splice(1, 1);
                }
                newAddress.city = address[1].split(" ")[1];
                let postalCode = address[1].split(" ")[0];
                if (postalCode.startsWith(response.countryCode)) {
                   postalCode = postalCode.slice(3);
                }
                newAddress.postalCode = postalCode;

                addresses.push(newAddress);
                partnerModel.refresh(true);
            }

            source.setValueState("Success")
                .setShowValueStateMessage(false)
                .setValueStateText("");
            source.setBusy(false).setBusyIndicatorDelay(1000);
            source.setValue(`${response.countryCode}${response.vatNumber}`);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            source.setBusy(false).setBusyIndicatorDelay(1000);
        });
    }

    public onPressAddAddress(): void {
        const model = this.getModel("salesDistributionBusinessPartnerDetail");
        const partner = model.getData() as IBusinessPartner;
        partner.addresses.push(this.getBlankAddress());
        model.refresh(true);
    }

    public onDeleteAddress(event: ListBase$DeleteEvent): void {
        const context = event.getParameter("listItem").getBindingContext("salesDistributionBusinessPartnerDetail");
        const model = context.getModel() as JSONModel;
        const index = Number(context.getPath().split("/").pop());
        const partner = model.getData() as IBusinessPartner;
        if (index > -1) {
            partner.addresses.splice(index, 1);
            model.refresh(true);
        }
    }

    /* ---------------------------------------------------------------------- *
     *  Sales area maintenance
     * ---------------------------------------------------------------------- */

    public onPressAddSalesArea(): void {
        const model = this.getModel("salesDistributionBusinessPartnerDetail");
        const partner = model.getData() as IBusinessPartner;
        const blankSalesArea = this.getBlankSalesArea();
        blankSalesArea.salesOrganization = ((partner.salesAreas.length + 1) * 1000).toString();
        blankSalesArea.distributionChannel = ((partner.salesAreas.length + 1) * 10).toString();
        partner.salesAreas.push(blankSalesArea);
        model.refresh(true);
    }

    public onDeleteSalesArea(event: ListBase$DeleteEvent): void {
        const context = event.getParameter("listItem").getBindingContext("salesDistributionBusinessPartnerDetail");
        const model = context.getModel() as JSONModel;
        const index = Number(context.getPath().split("/").pop());
        const partner = model.getData() as IBusinessPartner;
        if (index > -1) {
            partner.salesAreas.splice(index, 1);
            model.refresh(true);
        }
    }

    public onChangeSalesOrganization(event: Select$ChangeEvent): void {
        const context = event.getParameter("selectedItem").getBindingContext("salesDistributionBusinessPartnerDetail");
        const model = context.getModel() as JSONModel;
        const salesArea = model.getProperty(context.getPath());
        model.setProperty(`${context.getPath()}/distributionChannel`, salesArea.salesOrganization.substring(0,2));
        model.setProperty(`${context.getPath()}/division`, salesArea.salesOrganization.substring(2,4));
    }

    public onValueHelpRequestPaymentTerms(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("niteroiSalesDistributionPaymentTermsDialog") as TableSelectDialog;
        if (dialog) {
            const input = event.getSource();
            dialog.open(input.getValue());
            const inputBindingContext = input.getBindingContext("salesDistributionBusinessPartnerDetail");
            this._valueHelpRequestPath = inputBindingContext.getPath();
        }
    }

    public onValueHelpConfirmPaymentTerms(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItem = event.getParameter("selectedItem");

        if (selectedItem) {
            const context = selectedItem.getBindingContext("salesDistributionCodes");
            if (context && this._valueHelpRequestPath) {
                const selectedPaymentTerm = context.getObject() as IPaymentTerms;
                const businessPartnerModel = this.getModel("salesDistributionBusinessPartnerDetail");
                businessPartnerModel.setProperty(`${this._valueHelpRequestPath}/paymentTerms`, selectedPaymentTerm.code);
                this._valueHelpRequestPath = null;
            }
        }
    }

    /* ---------------------------------------------------------------------- *
     *  Analytics dashboard
     * ---------------------------------------------------------------------- */

    public onAnalyticsTilePress(event: Event): void {
        const tile = event.getSource() as GenericTile;
        const filterData = (tile.getCustomData() as CustomData[]).find((data) => data.getKey() === "filterType");
        if (!filterData) {
            return;
        }
        const filterType = filterData.getValue() as string;
        const table = this.byId("idBusinessPartnersTable") as Table;
        const binding = table?.getBinding("rows") as ListBinding;
        if (!binding) {
            return;
        }

        const filters: Filter[] = [];
        const sorters: Sorter[] = [];

        switch (filterType) {
            case "BLOCKED":
                filters.push(new Filter("isActive", FilterOperator.EQ, false));
                break;
            case "MISSING_EMAIL":
                filters.push(new Filter({
                    path: "addresses",
                    test: (addresses: IBusinessPartnerAddress[]) => !this.hasEmail(addresses)
                }));
                break;
            case "TOTAL":
            default:
                sorters.push(new Sorter("legalName1", false));
                break;
        }

        binding.filter(filters);
        binding.sort(sorters);
    }

    private updateAnalytics(): void {
        const analyticsModel = this.getModel("salesDistributionBusinessPartnersAnalytics");
        if (!analyticsModel) {
            return;
        }
        const partners = (this.getModel("salesDistributionBusinessPartnerCollection")?.getData() as IBusinessPartner[]) ?? [];
        if (!Array.isArray(partners) || partners.length === 0) {
            analyticsModel.setData(this.getDefaultAnalytics());
            return;
        }

        const totalPartners = partners.length;
        const blockedPartners = partners.filter((partner) => partner.isActive === false).length;
        const missingEmailPartners = partners.filter((partner) => !this.hasEmail(partner.addresses)).length;

        analyticsModel.setData({
            totalPartners,
            blockedPartners: blockedPartners,
            activeRatio: Math.round(((totalPartners - blockedPartners) / totalPartners) * 100),
            missingEmailPartners
        });
    }

    private getDefaultAnalytics(): object {
        return {
            totalPartners: 0,
            blockedPartners: 0,
            activeRatio: 0,
            missingEmailPartners: 0
        };
    }

    /* ---------------------------------------------------------------------- *
     *  Helpers
     * ---------------------------------------------------------------------- */

    private hasEmail(addresses: IBusinessPartnerAddress[]): boolean {
        return Array.isArray(addresses) && addresses.some((address) => !!address.email);
    }

    private getBlankDocumentModel(): IBusinessPartner {
        return {
            businessPartnerId: "",
            customerNumber: "",
            legalName1: "",
            legalName2: "",
            vatId: "",
            taxNumber: "",
            partnerRole: "CUSTOMER",
            isActive: true,
            createdAt: "",
            updatedAt: "",
            companyId: "",
            addresses: [],
            salesAreas: [
                this.getBlankSalesArea()
            ]
        };
    }

    private getBlankAddress(): IBusinessPartnerAddress {
        return {
            businessPartnerAddressId: "",
            addressType: "BILLING",
            street: "",
            postalCode: "",
            city: "",
            countryCode: "DE",
            email: "",
            phone: "",
            createdAt: "",
            updatedAt: ""
        };
    }

    private getBlankSalesArea(): IBusinessPartnerSalesArea {
        return {
            businessPartnerSalesAreaId: "",
            salesOrganization: "1000",
            distributionChannel: "10",
            division: "00",
            currency: "EUR",
            incoterm: "EXW",
            paymentTerms: "0001",
            creditLimit: "0.00",
            createdAt: "",
            updatedAt: ""
        };
    }
}
